import { getConfig } from '../config';
import { getImageMimeType, loadImage } from './image';

type RGB = { r: number; g: number; b: number };

const DEFAULT_MASK_COLOR: RGB = { r: 51, g: 136, b: 255 };
/**
 * 将颜色字符串转换为 RGB 对象
 * 支持 hex（#RRGGBB/#RGB）和 rgb(r, g, b) 格式
 * @param color - 输入颜色字符串
 * @returns RGB 对象
 */
function parseColor(color?: string): RGB {
    if (!color) return DEFAULT_MASK_COLOR;
    color = color.trim().toLowerCase();

    const hex6 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
    const hex3 = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;

    let result: RegExpExecArray | null;

    if ((result = hex6.exec(color))) {
        return {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        };
    }

    if ((result = hex3.exec(color))) {
        return {
            r: parseInt(result[1] + result[1], 16),
            g: parseInt(result[2] + result[2], 16),
            b: parseInt(result[3] + result[3], 16),
        };
    }

    if ((result = rgb.exec(color))) {
        return {
            r: parseInt(result[1]),
            g: parseInt(result[2]),
            b: parseInt(result[3]),
        };
    }

    return DEFAULT_MASK_COLOR;
}

export function canvasToFile(canvas: HTMLCanvasElement, mimeType?: string): Promise<File> {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                throw new Error('Canvas is empty or toBlob failed');
            }
            mimeType = mimeType || getImageMimeType(canvas.toDataURL());
            const file = new File([blob], `${Date.now()}.${mimeType.split('/')[1] || 'png'}`, { type: mimeType });
            resolve(file);
        });
    });
}

function isWithinTolerance(value: number, target: number, tolerance = 5): boolean {
    return Math.abs(value - target) <= tolerance;
}

export const segRecognizeMask = async (params: {
    maskURL: string;
    target: number;
    index?: number;
    fillColor?: string;
    transfer?: boolean;
}) => {
    const { maskURL, target, fillColor, index = 0 } = params;
    const image = await loadImage(maskURL);

    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = imageData;

    const { r, g, b } = parseColor(fillColor);

    // 分割RGB图， target 为目标
    for (let i = 0; i < data.length; i += 4) {
        if (isWithinTolerance(data[i + index], target)) {
            data[i + 0] = r;
            data[i + 1] = g;
            data[i + 2] = b;
        } else {
            data[i + 3] = 0;
        }
    }

    ctx.putImageData(imageData, 0, 0);

    return canvas.toDataURL();
};

export async function getMaskURLInfo(maskURL: string, value: number) {
    const image = await loadImage(maskURL);
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return { url: '', hasMask: false };

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = imageData;
    let hasMask = false;

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) {
            data[i + 0] = value;
            data[i + 1] = value;
            data[i + 2] = value;
            data[i + 3] = 255;
            hasMask = true;
        }
    }

    ctx.putImageData(imageData, 0, 0);

    const { uploadFileToObs } = getConfig();

    if (!uploadFileToObs || !hasMask) return { url: '', hasMask: false };

    const mimeType = getImageMimeType(maskURL);
    const file = await canvasToFile(canvas, mimeType);

    const { url } = await uploadFileToObs(file);

    return { url, hasMask };
}

export function createCanvasWithImage(image: HTMLImageElement) {
    const canvas = document.createElement('canvas') as HTMLCanvasElement;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.drawImage(image, 0, 0, image.width, image.height);

    return canvas;
}

export function getImageData(canvas: HTMLCanvasElement): ImageData {
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export function createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');

    canvas.width = width;
    canvas.height = height;

    return canvas;
}

export function isCanvas(image: HTMLImageElement | HTMLCanvasElement): image is HTMLCanvasElement {
    return image.tagName === 'CANVAS';
}

export async function mergeMaskToTranslucentMask(maskURL: string, translucentMaskURL: string): Promise<string> {
    const [maskImg, translucentImg] = await Promise.all([loadImage(maskURL), loadImage(translucentMaskURL)]);

    const width = maskImg.width;
    const height = maskImg.height;

    // 创建两个 canvas
    const maskCanvas = document.createElement('canvas');
    const transCanvas = document.createElement('canvas');
    maskCanvas.width = transCanvas.width = width;
    maskCanvas.height = transCanvas.height = height;

    const maskCtx = maskCanvas.getContext('2d')!;
    const transCtx = transCanvas.getContext('2d')!;

    maskCtx.drawImage(maskImg, 0, 0, width, height);
    transCtx.drawImage(translucentImg, 0, 0, width, height);

    const maskData = maskCtx.getImageData(0, 0, width, height).data;
    const transImageData = transCtx.getImageData(0, 0, width, height);
    const transData = transImageData.data;

    for (let i = 0; i < transData.length; i += 4) {
        const maskAlpha = maskData[i + 3];
        const isTransOpaque = transData[i + 1] > 0;

        if (maskAlpha > 0 && !isTransOpaque) {
            // mask 有值但 translucent 没有，新增R通道
            transData[i] = 255; // R
            // G/B 保持不变
        } else if (maskAlpha === 0 && isTransOpaque) {
            // translucent 有值但 mask 没有，删除RGB
            transData[i] = 0;
            transData[i + 1] = 0;
            transData[i + 2] = 0;
        }
    }

    transCtx.putImageData(transImageData, 0, 0);

    const mimeType = getImageMimeType(maskURL);
    const file = await canvasToFile(transCanvas, mimeType);

    const { uploadFileToObs } = getConfig();

    if (!uploadFileToObs) return '';

    const { url } = await uploadFileToObs(file);

    return url;
}
